"use client";

import React, { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const teamData = [{
  type: "person",
  name: "Jessica Gade",
  title: "DPT, CSMT | Clinical Specialist",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1639921/img/image_ax40rcfc.jpg",
  text: "Jessica is a Doctor of Physical Therapy and Certified Spinal Manual Therapist who translates her expertise in human rehabilitation to the canine world. She specializes in trail-tested recovery and mobility support for Durango's active and aging dogs."
}, {
  type: "quote",
  name: "Clinical Philosophy",
  title: "",
  image: "",
  text: "I believe in trail-tested recovery—combining physical therapy expertise with the healing power of specialized canine bodywork."
}, {
  type: "person",
  name: "Trail Recovery",
  title: "Performance & Mobility Support",
  image: "https://durangodogmassage.com/wp-content/uploads/2026/04/WhatsApp-Image-2026-04-19-at-21.11.12.jpeg?v=51p60c1hyzb",
  text: ""
}];
export default function MeetTheHandsThatShape() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="223215" id="the-hands-behind-the-healing" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={`transition-all duration-1000 ease-out mb-16 md:mb-24 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <h2 className="font-default text-5xl md:text-7xl lg:text-8xl tracking-tight leading-none">
            The Hands <br className="hidden md:block" />
            <span className="text-muted-foreground italic">Behind the Healing</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {teamData.map((item, i) => <div data-index={i} key={i} className="flex flex-col h-full">
              <GridItem item={item} index={i} />
            </div>)}
        </div>
      </div>
    </section>;
}
function GridItem({
  item,
  index
}: {
  item: typeof teamData[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const delay = index * 200;
  return <div ref={ref} className={`h-full transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`} style={{
    transitionDelay: `${delay}ms`
  }}>
      {item.type === "quote" ? <div className="bg-primary text-primary-foreground p-8 md:p-12 rounded-sm flex flex-col justify-center h-full min-h-[400px] shadow-md border-l-4 border-secondary">
          <span className="font-default text-xs md:text-sm uppercase tracking-widest mb-8 opacity-80">
            {item.name}
          </span>
          <p className="font-default text-3xl md:text-4xl lg:text-5xl leading-tight">
            "{item.text}"
          </p>
        </div> : <div className="group h-full flex flex-col">
          <div className="overflow-hidden rounded-sm aspect-[3/4] mb-6 bg-accent relative">
            <div className="absolute inset-0 bg-secondary/10 mix-blend-multiply z-10 pointer-events-none transition-opacity duration-700 group-hover:opacity-0"></div>
            <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-all duration-1000 ease-in-out group-hover:scale-105" />
          </div>
          <div className="mt-auto">
            <h3 className="font-default font-bold text-2xl md:text-3xl tracking-tight">
              {item.name}
            </h3>
            <p className="font-default text-sm text-muted-foreground mt-2 uppercase tracking-widest">
              {item.title}
            </p>
          </div>
        </div>}
    </div>;
}